import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { bemClassBuilder } from '@vista-digital/bem-class-builder';
import { getLastElement } from '@vista-digital/utilities';
import { AnimatePresence, motion } from 'framer-motion';
import { observer } from 'mobx-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAnimationConfig } from '../../../context/occ/hooks/use-animation-config';
import { getKeyCodeForEvent, KeyCode } from '../../../utils/key-code';
import { ModalMode } from '../types/mode';
import { ModalFooter } from './footer';
import { ModalHeader } from './header';
const b = bemClassBuilder('v-modal');
const animationDuration = 0.3;
export const ModalUi = observer(props => {
    const modalRef = useRef(null);
    const [isAnimating, setIsAnimating] = useState(false);
    const { show, onClose, onOpen, className, anchoredToTop = false, mode = ModalMode.Auto, heading, closeButtonIconName, canBeClosed, headerImageProps, header, footerLayout } = props;
    useEffect(() => {
        if (!show) {
            return;
        }
        onOpen === null || onOpen === void 0 ? void 0 : onOpen();
    }, [show, onOpen]);
    const handleClose = useCallback(() => {
        if (canBeClosed === false || !show) {
            return;
        }
        onClose === null || onClose === void 0 ? void 0 : onClose();
    }, [canBeClosed, show, onClose]);
    // Makes sure if the user presses tab or shift-tab, the focus stays in the modal.
    const handleTab = (event) => {
        if (!(modalRef === null || modalRef === void 0 ? void 0 : modalRef.current)) {
            return;
        }
        const focusableModalElements = Array.from(modalRef.current.querySelectorAll('a, button, textarea, input, select'));
        const firstElement = focusableModalElements[0];
        const lastElement = getLastElement(focusableModalElements);
        if (!event.shiftKey && document.activeElement === lastElement) {
            firstElement.focus();
            event.preventDefault();
        }
        else if (event.shiftKey && document.activeElement === firstElement) {
            lastElement.focus();
            event.preventDefault();
        }
    };
    const handleKeyDown = useCallback((event) => {
        if (isAnimating) {
            event.preventDefault();
        }
        const key = getKeyCodeForEvent(event);
        switch (key) {
            case KeyCode.Escape:
                handleClose();
                break;
            case KeyCode.Tab:
                handleTab(event);
                break;
            default:
        }
    }, [handleClose, isAnimating]);
    useEffect(() => {
        document.addEventListener('keydown', handleKeyDown, false);
        return () => {
            document.removeEventListener('keydown', handleKeyDown, false);
        };
    }, [handleKeyDown]);
    useEffect(() => {
        // This prevents scrolling of the body while the modal is open.
        document.body.style.overflow = show ? 'hidden' : 'unset';
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [show]);
    const { enableAnimations } = useAnimationConfig();
    const modalAnimationProps = enableAnimations
        ? {
            initial: { opacity: 0 },
            animate: { opacity: 1 },
            transition: { duration: animationDuration },
            exit: { opacity: 0, pointerEvents: 'none' },
            onAnimationStart: () => setIsAnimating(true),
            onAnimationComplete: () => setIsAnimating(false)
        }
        : {};
    const modalOverlayAnimationProps = enableAnimations
        ? {
            initial: { opacity: 0 },
            animate: { opacity: 1 },
            transition: { duration: animationDuration },
            exit: { opacity: 0 }
        }
        : {};
    return createPortal(_jsx(AnimatePresence, { onExitComplete: props.onAfterClose, children: show && (_jsxs(motion.aside, { className: b.block({ anchoredToTop }, className), "aria-modal": "true", "aria-hidden": !show, tabIndex: -1, role: "dialog", children: [_jsx(motion.div, Object.assign({}, modalOverlayAnimationProps, { className: b.element('background-overlay'), onClick: handleClose })), _jsxs(motion.div, Object.assign({}, modalAnimationProps, { className: b.element('container', { mode }), ref: modalRef, children: [_jsx(ModalHeader, { heading: heading, onClose: onClose, closeButtonIconName: closeButtonIconName, showCloseButton: canBeClosed, imageProps: headerImageProps, header: header }), _jsx("section", { className: b.element('body'), children: props.children }), _jsx(ModalFooter, { layout: footerLayout, children: props.footer })] }))] })) }), document.body);
});
ModalUi.displayName = 'ModalUi';
//# sourceMappingURL=ui.js.map